import React, { useState, useRef, useEffect } from 'react';
import TypeWriter from '../components/TypeWriter';
import './SignalHub.css';

// Backend API base (override with REACT_APP_API_BASE if needed)
// Backend API base (override with REACT_APP_API_BASE if needed)
const API_BASE = process.env.REACT_APP_API_BASE || 'https://bharathchilaka-test.hf.space';

  const MORSE_MAP = {
  A: '.-', B: '-...', C: '-.-.', D: '-..', E: '.', F: '..-.', G: '--.', H: '....', I: '..', J: '.---',
  K: '-.-', L: '.-..', M: '--', N: '-.', O: '---', P: '.--.', Q: '--.-', R: '.-.', S: '...', T: '-',
  U: '..-', V: '...-', W: '.--', X: '-..-', Y: '-.--', Z: '--..',
  '0': '-----','1': '.----','2': '..---','3': '...--','4': '....-','5': '.....','6': '-....','7': '--...','8': '---..','9': '----.'
};

const SignalHub = () => {
  const [text, setText] = useState('');
  const [morse, setMorse] = useState('');
  const [signSteps, setSignSteps] = useState([]);
  const [videoUrl, setVideoUrl] = useState('');
  const videoObjectUrlRef = useRef(null);
  const morseAudioUrlRef = useRef(null);
  const lastServerVideoPathRef = useRef(null);
  const [morsePrediction, setMorsePrediction] = useState('');
  const [uploadError, setUploadError] = useState('');
  const [isPlaying, setIsPlaying] = useState(false);
  const isPlayingRef = useRef(false);
  const audioStopTimeoutRef = useRef(null);
  const uploadInputRef = useRef(null);
  const videoUploadRef = useRef(null);
  const [videoUploadProgress, setVideoUploadProgress] = useState(0);
  const [videoPredictions, setVideoPredictions] = useState([]);
  const [videoThumbnails, setVideoThumbnails] = useState([]);

  // reverse morse map for decoding
  const REVERSE_MORSE = Object.entries(MORSE_MAP).reduce((acc, [k, v]) => { acc[v] = k; return acc; }, {});

  // Revoke object URL on unmount to avoid memory leaks
  useEffect(() => {
    return () => {
      if (videoObjectUrlRef.current) {
        try { URL.revokeObjectURL(videoObjectUrlRef.current); } catch (e) { /* ignore */ }
        videoObjectUrlRef.current = null;
      }
      if (morseAudioUrlRef.current) {
        try { URL.revokeObjectURL(morseAudioUrlRef.current); } catch (e) { /* ignore */ }
        morseAudioUrlRef.current = null;
      }
      // clear any scheduled stop for playback
      if (audioStopTimeoutRef.current) {
        clearTimeout(audioStopTimeoutRef.current);
        audioStopTimeoutRef.current = null;
      }
      isPlayingRef.current = false;
      setIsPlaying(false);
    };
  }, []);

  // Expanded ASL emoji dictionary with descriptions (phrases prioritized)
  const EMOJI_DICT = {
    'I LOVE YOU': { emoji: '🤟', desc: "I Love You — Extend thumb, index finger, and pinky; keep middle and ring fingers folded. Combination of the letters I, L, and Y." },
    'I': { emoji: '🤟', desc: "I Love You — (also used standalone in some contexts)" },
    'LOVE': { emoji: '❤️', desc: 'Love — Cross your arms over your chest (hug gesture).' },
    'HELLO': { emoji: '👋', desc: 'Hello — Wave your hand outward from your forehead (like a casual salute).' },
    'THANK YOU': { emoji: '🤚', desc: 'Thank You — Place fingertips on your chin and move your hand forward, away from your face.' },
    'SORRY': { emoji: '✋➡️🤚', desc: 'Sorry — Make a fist and move it in small circles over your chest.' },
    'PEACE': { emoji: '✌️', desc: 'Peace / Goodbye (casual) — Same as the peace symbol.' },
    'PLEASE': { emoji: '🤲', desc: 'Please — Place flat hand on your chest and move it in a circular motion.' },
    'YES': { emoji: '✊➡️👆', desc: 'Yes — Make a fist and nod it up and down.' },
    'NO': { emoji: '✋✋', desc: 'No — Tap index and middle fingers together with thumb — like closing a duck’s beak twice.' },
    'ME': { emoji: '👉', desc: 'Me — Point to yourself.' },
    'YOU': { emoji: '👉', desc: 'You — Point to the person.' },
    'CALL ME': { emoji: '📞', desc: 'Call Me — Extend thumb and pinky (like a phone) and move it near your ear.' },
    'SLEEP': { emoji: '💤', desc: 'Sleep — Open hand near face, slowly close it as you bring it downward (like eyes closing).' },
    'EAT': { emoji: '🍽️', desc: 'Eat / Food — Bring your fingers together and tap them to your mouth.' },
    'FOOD': { emoji: '🍽️', desc: 'Eat / Food — Bring your fingers together and tap them to your mouth.' },
    'WALK': { emoji: '🚶‍♂️', desc: 'Go / Walk — Move two fingers forward like walking legs.' },
    'GO': { emoji: '🚶‍♂️', desc: 'Go / Walk — Move two fingers forward like walking legs.' },
    'HELP': { emoji: '🙋‍♂️', desc: 'Help — Place one fist in your other palm and move both hands upward slightly.' }
  };

  const handleMorse = () => {
    const cleaned = text.toUpperCase();
    const encoded = cleaned.split('').map(ch => MORSE_MAP[ch] || (ch === ' ' ? '/' : '?')).join(' ');
    setMorse(encoded);
  };

  const handleSignGenerate = () => {
    const cleaned = text.toUpperCase().trim();
    if (!cleaned) { setSignSteps([]); return; }

    const words = cleaned.split(/\s+/);
    const steps = [];
    let i = 0;
    while (i < words.length) {
      // try two-word phrase first
      let token = words[i];
      const two = i + 1 < words.length ? `${words[i]} ${words[i + 1]}` : null;
      if (two && EMOJI_DICT[two]) {
        steps.push({ char: EMOJI_DICT[two].emoji, step: EMOJI_DICT[two].desc });
        i += 2; // consume two words
        continue;
      }

      // single word match
      if (EMOJI_DICT[token]) {
        steps.push({ char: EMOJI_DICT[token].emoji, step: EMOJI_DICT[token].desc });
        i += 1;
        continue;
      }

      // no word-level match: break into characters
      for (const ch of token) {
        steps.push({ char: ch, step: `Gesture for '${ch}'` });
      }
      i += 1;
    }

    setSignSteps(steps);
  };

  const copyMorseToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(morse);
      alert('Morse code copied to clipboard');
    } catch (e) {
      console.error('Copy failed', e);
      alert('Failed to copy');
    }
  };

  // "Generate Frames" option removed; use Generate Video instead.

  const generateVideoFromBackend = async () => {
    if (!text.trim()) return;
    try {
      const res = await fetch(`${API_BASE}/api/generate_gesture_video`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ text })
      });
      if (!res.ok) throw new Error('Failed to generate video');
      const data = await res.json();
      if (data.video_url) {
        const videoPath = data.video_url.startsWith('http') ? data.video_url : `${API_BASE}${data.video_url}`;
        lastServerVideoPathRef.current = videoPath;
        // We will not attempt to preview here; only download will be supported.
        setVideoUrl(videoPath);
      }
    } catch (e) {
      console.error(e);
      alert('Failed to generate video');
    }
  };

  // no preview verification needed when preview is disabled

  // Frame-based playback has been removed; video generation is used instead.

  // When user clicks Download, ensure we have a blob URL to download (works around cross-origin download issues
  // by fetching the file and creating an object URL). If it's already a blob URL, allow the default behavior.
  const handleDownloadVideo = async (e) => {
    // if already a blob URL, allow browser to handle it
    if (videoUrl && videoUrl.startsWith('blob:')) return;
    e.preventDefault();
    try {
      const fetchUrl = videoUrl.startsWith('http') ? videoUrl : `${API_BASE}${videoUrl}`;
      const res = await fetch(fetchUrl);
      if (!res.ok) throw new Error('Failed to fetch video for download');
      const blob = await res.blob();
      // revoke previous object URL if any
      if (videoObjectUrlRef.current) {
        try { URL.revokeObjectURL(videoObjectUrlRef.current); } catch (e) { /* ignore */ }
        videoObjectUrlRef.current = null;
      }
      const url = URL.createObjectURL(blob);
      videoObjectUrlRef.current = url;
      setVideoUrl(url);
      // trigger download
      const a = document.createElement('a');
      a.href = url;
      a.download = 'gesture.mp4';
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (err) {
      console.error('Download failed', err);
      alert('Download failed. This can happen if the backend blocks cross-origin requests (CORS).');
    }
  };

  // Decode uploaded WAV (or other audio types) to predict Morse text
  const handleAudioUpload = async (e) => {
    setUploadError('');
    setMorsePrediction('');
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    try {
      const arrayBuffer = await file.arrayBuffer();
      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      const decoded = await audioCtx.decodeAudioData(arrayBuffer.slice(0));
      // convert to mono
      const channelData = decoded.numberOfChannels > 1 ? decoded.getChannelData(0) : decoded.getChannelData(0);
      const sampleRate = decoded.sampleRate;

      // compute envelope via short-time energy
      const frameSize = Math.floor(sampleRate * 0.01); // 10ms frames
      const energies = [];
      for (let i = 0; i < channelData.length; i += frameSize) {
        let sum = 0;
        for (let j = i; j < i + frameSize && j < channelData.length; j++) sum += Math.abs(channelData[j]);
        energies.push(sum / frameSize);
      }

      const maxE = Math.max(...energies);
      const thresh = Math.max(0.02 * maxE, 0.001); // adaptive threshold

      // binary signal: 1 = tone, 0 = silence
      const binary = energies.map(v => (v >= thresh ? 1 : 0));

      // run-length encode durations (in seconds)
      const runs = [];
      let cur = binary[0];
      let len = 1;
      for (let i = 1; i < binary.length; i++) {
        if (binary[i] === cur) len++; else { runs.push({val: cur, frames: len}); cur = binary[i]; len = 1; }
      }
      runs.push({val: cur, frames: len});

      // convert frames to seconds
      const runsSec = runs.map(r => ({ val: r.val, sec: (r.frames * frameSize) / sampleRate }));

      // estimate unit as the shortest tone duration
      const toneDurations = runsSec.filter(r => r.val === 1).map(r => r.sec).filter(s => s > 0);
      if (toneDurations.length === 0) throw new Error('No tone detected in audio');
      const unit = Math.min(...toneDurations);

      // build morse string
      let morseStr = '';
      for (let i = 0; i < runsSec.length; i++) {
        const r = runsSec[i];
        if (r.val === 1) {
          // tone
          morseStr += (r.sec > unit * 2 ? '-' : '.');
        } else {
          // silence: decide separation
          if (r.sec > unit * 3.5) {
            morseStr += ' / '; // word gap
          } else if (r.sec > unit * 1.9) {
            morseStr += ' '; // letter gap
          } else {
            // intra-character gap -> nothing
          }
        }
      }

      // normalize spaces
      morseStr = morseStr.replace(/\s+\/\s+/g, ' / ').replace(/\s+/g, ' ').trim();
      // map morse to text
      const words = morseStr.split(' / ').map(w => w.trim());
      const decodedWords = words.map(w => {
        if (!w) return '';
        return w.split(' ').map(letter => REVERSE_MORSE[letter] || '?').join('');
      });
      const predicted = decodedWords.join(' ').trim();
      setMorsePrediction(predicted || '[no decode]');
    } catch (err) {
      console.error('Audio decode failed', err);
      setUploadError('Failed to decode uploaded audio. Make sure it is a WAV generated by this app.');
    }
  };

  // Upload MP4 and get sign-gesture predictions from backend
  const handleVideoUpload = async (e) => {
    setVideoUploadProgress(0);
    setVideoPredictions([]);
    setVideoThumbnails([]);
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await fetch(`${API_BASE}/api/predict_sign_video`, { method: 'POST', body: form });
      if (!res.ok) throw new Error('Upload failed');
      const data = await res.json();
      setVideoPredictions(data.predictions || []);
      setVideoThumbnails(data.thumbnails || []);
      setVideoUploadProgress(100);
    } catch (err) {
      console.error('Video upload failed', err);
      alert('Video upload or prediction failed');
    }
  };

  // Morse audio playback
  const playMorseAudio = (wpm = 20) => {
    // prevent overlapping playback: ignore if already playing
    if (isPlayingRef.current) return;
    const unit = 1.2 / wpm; // seconds per dot approx
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    let t = ctx.currentTime;
    const oscillator = ctx.createOscillator();
    const gain = ctx.createGain();
    oscillator.type = 'sine';
    oscillator.frequency.value = 600;
    oscillator.connect(gain);
    gain.connect(ctx.destination);
    oscillator.start();

    isPlayingRef.current = true;
    setIsPlaying(true);

    const playTone = (duration) => {
      gain.gain.setValueAtTime(0.0001, t);
      gain.gain.exponentialRampToValueAtTime(0.5, t + 0.001);
      t += duration;
      gain.gain.exponentialRampToValueAtTime(0.0001, t);
    };

    const cleaned = (morse || '').trim();
    for (const ch of cleaned) {
      if (ch === '.') {
        playTone(unit);
        t += unit; // intra-character gap
      } else if (ch === '-') {
        playTone(unit * 3);
        t += unit;
      } else if (ch === ' ') {
        t += unit * 2; // gap between letters
      } else if (ch === '/') {
        t += unit * 4; // gap between words
      }
    }

    // stop oscillator after scheduled notes
    const stopAt = t + 0.1;
    oscillator.stop(stopAt);

    // schedule cleanup when playback should have finished
    const ms = Math.max(0, (stopAt - ctx.currentTime) * 1000);
    if (audioStopTimeoutRef.current) clearTimeout(audioStopTimeoutRef.current);
    audioStopTimeoutRef.current = setTimeout(() => {
      isPlayingRef.current = false;
      setIsPlaying(false);
      try { ctx.close(); } catch (e) { /* ignore */ }
      audioStopTimeoutRef.current = null;
    }, ms + 50);
  };

  // Generate Float32 samples for morse (sine wave) and return a WAV Blob
  const generateMorseWavBlob = (morseStr, wpm = 20, sampleRate = 44100) => {
    if (!morseStr) return null;
    const unit = 1.2 / wpm; // seconds per dot
    // Calculate total duration
    let totalSeconds = 0;
    for (const ch of morseStr) {
      if (ch === '.') totalSeconds += unit + unit; // tone + intra-char gap
      else if (ch === '-') totalSeconds += unit * 3 + unit;
      else if (ch === ' ') totalSeconds += unit * 2; // between letters
      else if (ch === '/') totalSeconds += unit * 4; // between words
    }
    totalSeconds += 0.1; // small tail

    const totalSamples = Math.ceil(totalSeconds * sampleRate);
    const samples = new Float32Array(totalSamples);
    let offset = 0;
    const twoPi = 2 * Math.PI;
    const freq = 600;
    const attack = Math.min(0.01, unit / 4);
    const release = attack;

    const writeSilence = (secs) => {
      const count = Math.floor(secs * sampleRate);
      offset += count;
    };

    const writeTone = (secs) => {
      const count = Math.floor(secs * sampleRate);
      for (let i = 0; i < count && (offset + i) < samples.length; i++) {
        const t = i / sampleRate;
        // simple envelope
        let env = 1.0;
        if (t < attack) env = t / attack;
        else if (t > secs - release) env = Math.max(0, (secs - t) / release);
        samples[offset + i] = env * 0.5 * Math.sin(twoPi * freq * t);
      }
      offset += count;
    };

    for (const ch of morseStr) {
      if (ch === '.') {
        writeTone(unit);
        writeSilence(unit); // intra-character gap
      } else if (ch === '-') {
        writeTone(unit * 3);
        writeSilence(unit);
      } else if (ch === ' ') {
        writeSilence(unit * 2);
      } else if (ch === '/') {
        writeSilence(unit * 4);
      }
    }

    // create WAV header and interleave samples as 16-bit PCM
    const wavBuffer = audioBufferToWav(samples, sampleRate);
    return new Blob([wavBuffer], { type: 'audio/wav' });
  };

  // Convert Float32Array to WAV ArrayBuffer (16-bit PCM mono)
  const audioBufferToWav = (float32Samples, sampleRate) => {
    const numChannels = 1;
    const sampleBits = 16;
    const blockAlign = numChannels * (sampleBits / 8);
    const byteRate = sampleRate * blockAlign;
    const dataSize = float32Samples.length * (sampleBits / 8);
    const buffer = new ArrayBuffer(44 + dataSize);
    const view = new DataView(buffer);

    /* RIFF identifier */ writeString(view, 0, 'RIFF');
    /* file length */ view.setUint32(4, 36 + dataSize, true);
    /* RIFF type */ writeString(view, 8, 'WAVE');
    /* format chunk identifier */ writeString(view, 12, 'fmt ');
    /* format chunk length */ view.setUint32(16, 16, true);
    /* sample format (raw) */ view.setUint16(20, 1, true);
    /* channel count */ view.setUint16(22, numChannels, true);
    /* sample rate */ view.setUint32(24, sampleRate, true);
    /* byte rate (sample rate * block align) */ view.setUint32(28, byteRate, true);
    /* block align (channel count * bytes per sample) */ view.setUint16(32, blockAlign, true);
    /* bits per sample */ view.setUint16(34, sampleBits, true);
    /* data chunk identifier */ writeString(view, 36, 'data');
    /* data chunk length */ view.setUint32(40, dataSize, true);

    // write samples
    let offset = 44;
    for (let i = 0; i < float32Samples.length; i++, offset += 2) {
      let s = Math.max(-1, Math.min(1, float32Samples[i]));
      view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
    }

    return buffer;
  };

  const writeString = (view, offset, string) => {
    for (let i = 0; i < string.length; i++) {
      view.setUint8(offset + i, string.charCodeAt(i));
    }
  };

  const handleDownloadMorseAudio = async (e) => {
    if (!morse) return;
    e.preventDefault();
    try {
      const blob = generateMorseWavBlob(morse);
      if (!blob) throw new Error('Failed to generate audio');
      // revoke previous audio URL if any
      if (morseAudioUrlRef.current) {
        try { URL.revokeObjectURL(morseAudioUrlRef.current); } catch (er) { /* ignore */ }
        morseAudioUrlRef.current = null;
      }
      const url = URL.createObjectURL(blob);
      morseAudioUrlRef.current = url;
      // trigger download
      const a = document.createElement('a');
      a.href = url;
      a.download = 'morse.wav';
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (err) {
      console.error('Morse audio download failed', err);
      alert('Failed to generate Morse audio');
    }
  };

  return (
    <div className="signal-hub">
      {/* Header */}
      <div className="signal-section">
        <div className="signal-header">
          <h1>
            <TypeWriter 
              text="🔗 Signal Hub" 
              speed={90} 
              cursorColor="white"
            />
          </h1>
          <p>Generate Morse, convert text to sign steps and manage gesture video assets</p>
        </div>

      </div>

      {/* Left column: Morse + Sign generators (side-by-side on wide screens) */}
      <div className="signal-section">
        <h2>Signal Hub</h2>

        <div className="generator-grid">
          <div className="generator-card">
            <h3>Morse Code Generator</h3>
            <textarea value={text} onChange={e => setText(e.target.value)} placeholder="Enter text to convert to Morse"></textarea>
            <div className="signal-controls">
              <button className="btn btn-primary" onClick={handleMorse}>Generate Morse</button>
              <button className="btn btn-secondary" onClick={() => { setText(''); setMorse(''); }}>Clear</button>
              <button className="btn" onClick={() => playMorseAudio()} disabled={!morse}>Play Morse Audio</button>
              <button className="btn" onClick={copyMorseToClipboard} disabled={!morse}>Copy Morse</button>
              <button className="btn" onClick={handleDownloadMorseAudio} disabled={!morse}>Download Morse Audio</button>
            </div>
            <div className="morse-output">{morse || <span className="muted">[no output]</span>}</div>
          </div>

          <div className="generator-card">
            <h3>Sign Gestures Generator (Text → Gestures)</h3>
            <textarea value={text} onChange={e => setText(e.target.value)} placeholder="Enter text to convert to sign gestures"></textarea>
            <div className="signal-controls">
              <button className="btn btn-primary" onClick={handleSignGenerate}>Generate Sign Steps</button>
              <button className="btn btn-secondary" onClick={() => setSignSteps([])}>Clear</button>
              <button className="btn" onClick={generateVideoFromBackend}>Generate Video (Backend)</button>
            </div>
            <div className="sign-steps">
              {signSteps.length === 0 && <div className="placeholder">No steps generated</div>}
              {signSteps.map((s, i) => (
                <div key={i} className="sign-step">
                  {s.char && s.char.length === 1 && s.char.match(/[A-Za-z0-9]/) ? (
                    <img src={`${API_BASE}/api/sign_image/${s.char.toLowerCase()}`} alt={`sign ${s.char}`} style={{ width: 72, height: 72 }} />
                  ) : (
                    <div className="sign-char">{s.char}</div>
                  )}
                  <div className="sign-desc">{s.step}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Right column: preview */}
      <div className="signal-section side">
        <div className="signal-controls" style={{ marginTop: '0.8rem' }}>
          {videoUrl && (
            <a href={videoUrl} className="btn btn-primary" download onClick={handleDownloadVideo}>Download Video</a>
          )}
        </div>

        <hr style={{ margin: '1rem 0' }} />

        {/* WAV upload area for Morse decoding */}
        <div className="upload-card">
          <h4>Upload Morse .wav to Decode</h4>
          <input ref={uploadInputRef} type="file" accept="audio/*,.wav" onChange={handleAudioUpload} />
          <div style={{ marginTop: '0.5rem' }}>
            <button className="btn btn-secondary" onClick={() => { if (uploadInputRef.current) uploadInputRef.current.value = ''; setMorsePrediction(''); setUploadError(''); }}>Clear Upload</button>
          </div>
          <div style={{ marginTop: '0.75rem' }}>
            <strong>Prediction:</strong>
            <div className="morse-output" style={{ marginTop: '0.25rem' }}>{morsePrediction || <span className="muted">[no upload yet]</span>}</div>
            {uploadError && <div className="error-text" style={{ marginTop: '0.5rem' }}>{uploadError}</div>}
          </div>
        </div>

        <hr style={{ margin: '1rem 0' }} />

        {/* MP4 upload UI removed */}
      </div>
    </div>
  );
};



export default SignalHub;
